export const SPEECH_AUDIO_STATUSES = {
  Idle: 'idle',
  Loading: 'loading',
  Ready: 'ready',
  Failed: 'failed',
} as const;

type SpeechAudioStatus =
  (typeof SPEECH_AUDIO_STATUSES)[keyof typeof SPEECH_AUDIO_STATUSES];

export const SPEECH_SYNTHESIS_FAILURE_REASONS = {
  Aborted: 'aborted',
  NoAudioReturned: 'noAudioReturned',
  RequestFailed: 'requestFailed',
  Unsupported: 'unsupported',
} as const;

type SpeechSynthesisFailureReason =
  (typeof SPEECH_SYNTHESIS_FAILURE_REASONS)[keyof typeof SPEECH_SYNTHESIS_FAILURE_REASONS];

export interface SpeechSynthesisFailure {
  readonly reason: SpeechSynthesisFailureReason;
  readonly message: string | null;
  readonly isRetryable: boolean;
}

export interface SpeechClip {
  readonly cacheKey: string;
  readonly status: SpeechAudioStatus;
  readonly url: string | null;
  readonly failure: SpeechSynthesisFailure | null;
}

export interface SpeechSynthesisRequest {
  readonly cacheKey: string;
  readonly text: string;
  readonly language?: string;
  readonly signal?: AbortSignal;
}

export interface SpeechSynthesisPort {
  readonly language: string | null;
  readonly clipFor: (cacheKey: string) => SpeechClip | null;
  readonly synthesize: (request: SpeechSynthesisRequest) => Promise<SpeechClip>;
  readonly hold: (cacheKey: string) => () => void;
  readonly evict: (cacheKey: string) => void;
  readonly abortAll: () => void;
  readonly dispose: () => void;
}

export const AUDIO_PLAYBACK_STATUSES = {
  Idle: 'idle',
  Playing: 'playing',
  Failed: 'failed',
} as const;

export type AudioPlaybackStatus =
  (typeof AUDIO_PLAYBACK_STATUSES)[keyof typeof AUDIO_PLAYBACK_STATUSES];

export const AUDIO_PLAYBACK_ENDINGS = {
  Completed: 'completed',
  Stopped: 'stopped',
  Superseded: 'superseded',
  Failed: 'failed',
} as const;

export type AudioPlaybackEnding =
  (typeof AUDIO_PLAYBACK_ENDINGS)[keyof typeof AUDIO_PLAYBACK_ENDINGS];

export interface AudioPlaybackCompletion {
  readonly playbackId: string;
  readonly ending: AudioPlaybackEnding;
  readonly message: string | null;
}

export interface AudioPlaybackRequest {
  readonly playbackId: string;
  readonly url: string;
  readonly onStarted?: () => void;
}

export interface AudioPlaybackPort {
  readonly status: AudioPlaybackStatus;
  readonly playbackId: string | null;
  readonly play: (
    request: AudioPlaybackRequest
  ) => Promise<AudioPlaybackCompletion>;
  readonly stop: (playbackId?: string) => void;
  readonly onEnded: (
    listener: (completion: AudioPlaybackCompletion) => void
  ) => () => void;
}

export interface SpeechToTextToken {
  readonly token: string;
  readonly expiresAt: string | null;
}

export interface SpeechToTextTokenPort {
  readonly isAvailable: boolean;
  readonly requestToken: () => Promise<SpeechToTextToken | null>;
}
